import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db, functions } from '../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Typography, Button, Backdrop, CircularProgress } from '@mui/material';
import {
  Container,
  StyledList,
  StyledListItem,
  Message,
  Actions
} from './Notifications.styles';
import useSnackbar from '../../hooks/useSnackbar';
import SnackbarAlert from '../SnackbarAlert/SnackbarAlert';
import { logError } from '../../utils/security';

const Notifications = ({ user, onProcessingChange }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const { openSnackbar, snackbarMessage, snackbarSeverity, showAlert, closeSnackbar } = useSnackbar();

  useEffect(() => {
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const notifList = [];
      
      for (const docSnap of querySnapshot.docs) {
        const notifData = { id: docSnap.id, ...docSnap.data() };
        
        // Prüfen, ob die zugehörige JoinRequest noch existiert und pending ist
        if (notifData.joinRequestId) {
          try {
            const joinReqSnap = await getDoc(doc(db, 'joinRequests', notifData.joinRequestId));
            if (joinReqSnap.exists() && joinReqSnap.data().status === 'pending') {
              // Aktuellen Benutzernamen vom Sender laden
              if (notifData.senderId) {
                try {
                  const senderSnap = await getDoc(doc(db, 'publicProfiles', notifData.senderId));
                  if (senderSnap.exists()) {
                    const currentUsername = senderSnap.data().username;
                    // Nachricht mit aktuellem Benutzernamen erstellen
                    notifData.message = t('joinRequestMessage', { username: currentUsername });
                  }
                } catch (error) {
                  console.error('Error fetching sender username:', error);
                  // Fallback: ursprüngliche Nachricht verwenden
                }
              }
              notifList.push(notifData);
            }
          } catch (error) {
            // JoinRequest existiert nicht mehr - Benachrichtigung nicht anzeigen
            console.log('JoinRequest nicht gefunden:', notifData.joinRequestId);
          }
        } else {
          // Benachrichtigungen ohne joinRequestId (falls vorhanden) immer anzeigen
          notifList.push(notifData);
        }
      }
      
      setNotifications(notifList);
    });
    return () => unsubscribe();
  }, [user.uid, t]);  
  
  
const handleAccept = async (notif) => {
    try {
      setProcessingId(notif.id);
      if (onProcessingChange) onProcessingChange(true);
      console.log('Starting accept process for notification:', notif.id);

      const acceptFn = httpsCallable(functions, 'acceptJoinRequest');
      const result = await acceptFn({ joinRequestId: notif.joinRequestId });

      console.log('Accept process completed successfully');
      const teamName = result?.data?.teamName || '';
      showAlert(t('acceptSuccess', { teamName }), 'info');
    } catch (error) {
      logError(error, 'ACCEPT_TEAM_MEMBER');
      console.error('Full accept error:', error);
      showAlert(t('acceptError'), 'error');
    } finally {
      setProcessingId(null);
      if (onProcessingChange) onProcessingChange(false);
    }
  };

  const handleReject = async (notif) => {
    try {
      setProcessingId(notif.id);
      if (onProcessingChange) onProcessingChange(true);
      console.log('Starting reject process for notification:', notif.id);
      
      // JoinRequest ablehnen
      const joinReqRef = doc(db, 'joinRequests', notif.joinRequestId);
      console.log('Rejecting join request:', notif.joinRequestId);
      await updateDoc(joinReqRef, { status: 'rejected' });

      // Benachrichtigung als gelesen markieren
      const notifRef = doc(db, 'notifications', notif.id);
      console.log('Marking notification as read:', notif.id);
      await updateDoc(notifRef, { read: true });

      console.log('Reject process completed successfully');
      showAlert(t('rejectSuccess'), 'info');
    } catch (error) {
      logError(error, 'REJECT_TEAM_MEMBER');
      console.error('Full reject error:', error);
      showAlert(t('rejectError'), 'error');
    } finally {
      setProcessingId(null);
      if (onProcessingChange) onProcessingChange(false);
    }
  };

  return (
    <Container>
      {notifications.length === 0 ? (
        <Typography variant="h5">{t('noNotifications')}</Typography>
      ) : (
        <StyledList>
          {notifications.map((notif) => (
            <StyledListItem key={notif.id} divider>
              <Message variant="body1">
                {notif.message}
              </Message>
              <Actions>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleAccept(notif)}
                  disabled={Boolean(processingId)}
                >
                  {t('accept')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleReject(notif)}
                  disabled={Boolean(processingId)}
                >
                  {t('reject')}
                </Button>
              </Actions>
            </StyledListItem>
          ))}
        </StyledList>
      )}
      <SnackbarAlert
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={closeSnackbar}
      />
      <Backdrop open={Boolean(processingId)} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default Notifications;